import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFlaggedSubmissions,
  removeFlaggedSubmission,
} from "../../redux/slice/flaggedSlice";
import { Api } from "../../network/api";

const FlaggedEssays = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const flaggedSubmissions = useSelector(selectFlaggedSubmissions);
  const [apiFlaggedSubmissions, setApiFlaggedSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFlaggedSubmissions = async () => {
      try {
        setLoading(true);
        const response = await Api.getFlag();
        const mappedSubmissions =
          response?.data?.flagged_submissions?.map((submission) => ({
            id: submission.id,
            userName: submission.user?.name || "N/A",
            assignmentName: submission.subject || "N/A",
            submissionDate: submission.submission_date
              ? new Date(submission.submission_date).toLocaleDateString()
              : "N/A",
            status: submission.status || "pending",
            flaggedAt: submission.flagged_at
              ? new Date(submission.flagged_at).toLocaleDateString()
              : "N/A",
            score: submission.score || 0,
            flagReasons: submission.flag_reasons || [],
          })) || [];

        setApiFlaggedSubmissions(mappedSubmissions);
      } catch (error) {
        console.error("Error fetching flagged submissions:", error);
        setApiFlaggedSubmissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFlaggedSubmissions();
  }, []);

  const handleOpenDetails = (submission) => {
    navigate(`/submissions/${submission.id}`);
  };

  const handleUnflag = async (submissionId) => {
    try {
      await Api.removeFlag(submissionId);
      dispatch(removeFlaggedSubmission(submissionId));
      setApiFlaggedSubmissions((prev) =>
        prev.filter((sub) => sub.id !== submissionId)
      );

      console.log("Submission unflagged successfully:", submissionId);
    } catch (error) {
      console.error("Error unflagging submission:", error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Flagged Essays</h1>
          <p className="text-gray-600 mt-1">
            Review and manage flagged essay submissions.
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Loading flagged essays...
              </h3>
              <p className="text-gray-600 mb-6">
                Please wait while we fetch the flagged submissions.
              </p>
            </div>
          ) : apiFlaggedSubmissions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Name
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assignment Name
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submission Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Flagged Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {apiFlaggedSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {submission.userName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {submission.assignmentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {submission.submissionDate}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            submission.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : submission.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : submission.status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {submission.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {submission.flaggedAt}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleOpenDetails(submission)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                          >
                            Details
                          </button>
                          <button
                            onClick={() => handleUnflag(submission.id)}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                          >
                            Unflag
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No flagged essays
              </h3>
              <p className="text-gray-600 mb-6">
                All submissions are currently unflagged.
              </p>
              <button
                onClick={() => navigate("/submissions")}
                className="bg-gradient-to-r from-red-600 to-red-800 text-white px-6 py-3 rounded-2xl font-medium shadow-lg hover:from-red-700 hover:to-red-900 hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                View Submissions
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlaggedEssays;
